import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils/cn';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

type AspectRatio = '16:9' | '4:3' | '1:1' | '9:16' | '21:9';
type VideoSource = 'url' | 'youtube' | 'vimeo' | 'wistia' | 'dailymotion' | 'upload';
type PlayerTheme = 'default' | 'minimal' | 'custom';

interface VideoConfig {
  videoSource?: VideoSource;
  videoUrl?: string;
  videoId?: string;
  thumbnailUrl?: string;
  videoTitle?: string;
  videoDescription?: string;
  videoDuration?: number;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  showPlaybackSpeed?: boolean;
  defaultSpeed?: number;
  allowFullscreen?: boolean;
  startTime?: number;
  endTime?: number;
  requireFullView?: boolean;
  minWatchPercentage?: number;
  allowSkipping?: boolean;
  allowRewinding?: boolean;
  aspectRatio?: AspectRatio;
  videoMaxWidth?: 'small' | 'medium' | 'large' | 'full';
  playerTheme?: PlayerTheme;
  showTitle?: boolean;
  showDescription?: boolean;
  showDuration?: boolean;
  showProgress?: boolean;
  showTimestamps?: boolean;
  videoBorderRadius?: 'none' | 'small' | 'medium' | 'large';
  videoShadow?: 'none' | 'small' | 'medium' | 'large';
  // Legacy field
  mediaUrl?: string;
}

const ASPECT_RATIO_VALUES: Record<AspectRatio, number> = {
  '16:9': 16 / 9,
  '4:3': 4 / 3,
  '1:1': 1,
  '9:16': 9 / 16,
  '21:9': 21 / 9,
};

const MAX_WIDTH_VALUES = {
  small: '480px',
  medium: '640px',
  large: '854px',
  full: '100%',
};

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

/**
 * Extract video ID from various URL formats
 */
function extractVideoId(url: string, source: VideoSource): string | null {
  if (!url) return null;

  switch (source) {
    case 'youtube': {
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /^([a-zA-Z0-9_-]{11})$/,
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      break;
    }
    case 'vimeo': {
      const patterns = [
        /vimeo\.com\/(?:video\/)?(\d+)/,
        /^(\d+)$/,
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      break;
    }
    case 'wistia': {
      const patterns = [
        /wistia\.com\/medias\/([a-zA-Z0-9]+)/,
        /^([a-zA-Z0-9]+)$/,
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      break;
    }
    case 'dailymotion': {
      const patterns = [
        /dailymotion\.com\/video\/([a-zA-Z0-9]+)/,
        /^([a-zA-Z0-9]+)$/,
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      break;
    }
  }
  return null;
}

/**
 * Build embed URL for video platforms
 */
function buildEmbedUrl(
  source: VideoSource,
  videoId: string,
  options?: { autoplay?: boolean; muted?: boolean; loop?: boolean; startTime?: number }
): string {
  const params = new URLSearchParams();

  if (options?.autoplay) params.set('autoplay', '1');
  if (options?.muted) params.set('muted', '1');
  if (options?.loop) params.set('loop', '1');

  switch (source) {
    case 'youtube':
      if (options?.muted) params.set('mute', '1');
      if (options?.startTime) params.set('start', String(options.startTime));
      params.set('rel', '0');
      params.set('modestbranding', '1');
      params.set('enablejsapi', '1');
      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    case 'vimeo':
      if (options?.startTime) params.set('t', `${options.startTime}s`);
      return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
    case 'wistia':
      return `https://fast.wistia.net/embed/iframe/${videoId}?${params.toString()}`;
    case 'dailymotion':
      if (options?.startTime) params.set('start', String(options.startTime));
      return `https://www.dailymotion.com/embed/video/${videoId}?${params.toString()}`;
    default:
      return '';
  }
}

/**
 * Get YouTube thumbnail URL
 */
function getYouTubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}

/**
 * Video stimulus question component.
 * Displays a video for viewing with comprehensive playback controls and tracking.
 */
export function Video({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<string | null>) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [hasWatched, setHasWatched] = useState(value === 'watched');
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isMuted, setIsMuted] = useState(false);

  // Config
  const cfg = (question.config || {}) as VideoConfig;
  const videoSource = cfg.videoSource || 'url';
  const videoUrl = cfg.videoUrl || cfg.mediaUrl || '';
  const autoplay = cfg.autoplay ?? false;
  const muted = cfg.muted ?? false;
  const loop = cfg.loop ?? false;
  const showPlayerControls = cfg.controls !== false;
  const showPlaybackSpeed = cfg.showPlaybackSpeed ?? false;
  const defaultSpeed = cfg.defaultSpeed ?? 1;
  const allowFullscreen = cfg.allowFullscreen !== false;
  const startTime = cfg.startTime;
  const endTime = cfg.endTime;
  const requireFullView = cfg.requireFullView ?? false;
  const minWatchPercentage = cfg.minWatchPercentage;
  const allowSkipping = cfg.allowSkipping !== false;
  const allowRewinding = cfg.allowRewinding !== false;
  const aspectRatio = cfg.aspectRatio || '16:9';
  const maxWidth = cfg.videoMaxWidth || 'large';
  const theme = cfg.playerTheme || 'default';
  const showTitle = cfg.showTitle !== false;
  const showDescription = cfg.showDescription !== false;
  const showDuration = cfg.showDuration !== false;
  const showProgress = cfg.showProgress !== false;
  const showTimestamps = cfg.showTimestamps !== false;
  const borderRadius = cfg.videoBorderRadius || 'medium';
  const shadow = cfg.videoShadow || 'medium';
  const thumbnailUrl = cfg.thumbnailUrl;
  const videoTitle = cfg.videoTitle;
  const videoDescription = cfg.videoDescription;
  const videoDuration = cfg.videoDuration;

  // Determine if using embed (YouTube, Vimeo, etc.) or native video
  const isEmbed = videoSource !== 'url' && videoSource !== 'upload';
  const videoId = isEmbed ? extractVideoId(videoUrl, videoSource) : null;

  // Set initial states
  useEffect(() => {
    setPlaybackSpeed(defaultSpeed);
    setIsMuted(muted);
  }, [defaultSpeed, muted]);

  useEffect(() => {
    if (hasWatched) {
      onChange('watched');
    }
  }, [hasWatched, onChange]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      let dur = videoRef.current.duration;
      if (startTime !== undefined) {
        videoRef.current.currentTime = startTime;
      }
      if (endTime !== undefined) {
        dur = endTime - (startTime || 0);
      }
      setDuration(dur);
    }
  }, [startTime, endTime]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      setCurrentTime(current);

      // Calculate progress based on effective duration
      const effectiveStart = startTime || 0;
      const effectiveEnd = endTime || videoRef.current.duration;
      const effectiveDuration = effectiveEnd - effectiveStart;
      const currentProgress = ((current - effectiveStart) / effectiveDuration) * 100;
      setProgress(Math.min(100, Math.max(0, currentProgress)));

      // Check if should stop at endTime
      if (endTime !== undefined && current >= endTime) {
        videoRef.current.pause();
        setIsPlaying(false);
        if (!loop) {
          setHasWatched(true);
        }
      }

      // Mark as watched based on watch percentage
      const watchThreshold = requireFullView ? 95 : (minWatchPercentage || 90);
      if (currentProgress >= watchThreshold && !hasWatched) {
        setHasWatched(true);
      }
    }
  }, [startTime, endTime, loop, requireFullView, minWatchPercentage, hasWatched]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setHasWatched(true);
  }, []);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);

  const handleError = useCallback(() => {
    setError('Failed to load video. Please try again later.');
  }, []);

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current && (allowSkipping || allowRewinding)) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      const effectiveStart = startTime || 0;
      const effectiveEnd = endTime || videoRef.current.duration;
      const effectiveDuration = effectiveEnd - effectiveStart;
      const newTime = effectiveStart + (percentage * effectiveDuration);

      // Restrict seeking based on settings
      if (!allowSkipping && newTime > videoRef.current.currentTime) return;
      if (!allowRewinding && newTime < videoRef.current.currentTime) return;

      videoRef.current.currentTime = newTime;
    }
  }, [allowSkipping, allowRewinding, startTime, endTime]);

  const changeSpeed = useCallback((speed: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackSpeed(speed);
      setShowSpeedMenu(false);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!allowFullscreen) return;

    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, [allowFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Style classes
  const borderRadiusClass = {
    none: 'rounded-none',
    small: 'rounded',
    medium: 'rounded-lg',
    large: 'rounded-xl',
  }[borderRadius];

  const shadowClass = {
    none: '',
    small: 'shadow-sm',
    medium: 'shadow-md',
    large: 'shadow-lg',
  }[shadow];

  const aspectRatioValue = ASPECT_RATIO_VALUES[aspectRatio];
  const paddingBottom = `${(1 / aspectRatioValue) * 100}%`;

  // No video configured
  if (!videoUrl && !videoId) {
    return (
      <QuestionWrapper question={question} errors={errors}>
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No video configured for this question</p>
        </div>
      </QuestionWrapper>
    );
  }

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div
        className="space-y-4"
        style={{ maxWidth: MAX_WIDTH_VALUES[maxWidth], margin: '0 auto' }}
      >
        {/* Video Title */}
        {showTitle && videoTitle && (
          <h3 className="font-medium text-base text-gray-900 dark:text-gray-100">
            {videoTitle}
          </h3>
        )}

        {/* Video Player Container */}
        <div
          ref={containerRef}
          className={cn('relative overflow-hidden bg-black', borderRadiusClass, shadowClass)}
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(isPlaying ? false : true)}
        >
          {error ? (
            <div
              className="flex items-center justify-center bg-gray-900"
              style={{ paddingBottom }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <svg
                    className="mx-auto h-12 w-12 text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="mt-2 text-sm">{error}</p>
                </div>
              </div>
            </div>
          ) : isEmbed && videoId ? (
            // Embedded video (YouTube, Vimeo, etc.)
            <div className="relative" style={{ paddingBottom }}>
              <iframe
                ref={iframeRef}
                src={buildEmbedUrl(videoSource, videoId, { autoplay, muted, loop, startTime })}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen={allowFullscreen}
                title={videoTitle || 'Video'}
              />
            </div>
          ) : (
            // Native video player
            <>
              <div className="relative" style={{ paddingBottom }}>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  className="absolute inset-0 w-full h-full object-contain"
                  autoPlay={autoplay}
                  muted={muted}
                  loop={loop}
                  playsInline
                  poster={thumbnailUrl || (videoId && videoSource === 'youtube' ? getYouTubeThumbnail(videoId) : undefined)}
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleEnded}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onError={handleError}
                >
                  Your browser does not support the video tag.
                </video>

                {/* Play/Pause overlay */}
                <div
                  className={cn(
                    'absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity',
                    isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'
                  )}
                  onClick={togglePlay}
                >
                  <button
                    type="button"
                    disabled={disabled}
                    className={cn(
                      'flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow-lg transition-transform hover:scale-110',
                      disabled && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {isPlaying ? (
                      <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                      </svg>
                    ) : (
                      <svg className="h-8 w-8 pl-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Duration badge */}
                {showDuration && (duration || videoDuration) && (
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs">
                    {formatTime(duration || videoDuration || 0)}
                  </div>
                )}
              </div>

              {/* Custom controls bar */}
              {showPlayerControls && (
                <div
                  className={cn(
                    'absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 transition-opacity',
                    showControls || !isPlaying ? 'opacity-100' : 'opacity-0',
                    theme === 'minimal' && 'bg-black/40'
                  )}
                >
                  {/* Progress bar */}
                  {showProgress && (
                    <div
                      className={cn(
                        'h-1 w-full rounded-full bg-white/30 mb-2 cursor-pointer',
                        (!allowSkipping && !allowRewinding) && 'cursor-default'
                      )}
                      onClick={handleSeek}
                    >
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all duration-100"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between text-white text-xs">
                    <div className="flex items-center gap-2">
                      {/* Play/Pause button */}
                      <button
                        type="button"
                        onClick={togglePlay}
                        disabled={disabled}
                        className="p-1 hover:bg-white/20 rounded"
                      >
                        {isPlaying ? (
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>

                      {/* Mute button */}
                      <button
                        type="button"
                        onClick={toggleMute}
                        className="p-1 hover:bg-white/20 rounded"
                      >
                        {isMuted ? (
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                          </svg>
                        )}
                      </button>

                      {/* Timestamps */}
                      {showTimestamps && (
                        <span className="text-[10px] tabular-nums">
                          {formatTime(currentTime)} / {formatTime(duration || videoDuration || 0)}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Playback speed */}
                      {showPlaybackSpeed && (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                            className="px-1.5 py-0.5 hover:bg-white/20 rounded text-[10px]"
                          >
                            {playbackSpeed}x
                          </button>
                          {showSpeedMenu && (
                            <div className="absolute bottom-full right-0 mb-1 bg-black/90 rounded py-1 min-w-[60px]">
                              {PLAYBACK_SPEEDS.map((speed) => (
                                <button
                                  key={speed}
                                  type="button"
                                  onClick={() => changeSpeed(speed)}
                                  className={cn(
                                    'block w-full px-2 py-1 text-left text-[10px] hover:bg-white/20',
                                    playbackSpeed === speed && 'text-primary-400'
                                  )}
                                >
                                  {speed}x
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Fullscreen button */}
                      {allowFullscreen && (
                        <button
                          type="button"
                          onClick={toggleFullscreen}
                          className="p-1 hover:bg-white/20 rounded"
                        >
                          {isFullscreen ? (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Video Description */}
        {showDescription && videoDescription && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {videoDescription}
          </p>
        )}

        {/* Progress bar (external) - only for embeds where we can't control */}
        {isEmbed && showProgress && (
          <div className="space-y-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-300"
                style={{ width: `${hasWatched ? 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Watch status */}
        {requireFullView && (
          <div
            className={cn(
              'flex items-center gap-2 rounded-lg p-3 transition-colors',
              hasWatched
                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            )}
          >
            {hasWatched ? (
              <>
                <svg className="h-5 w-5 text-green-500 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium">Video watched - you may continue</span>
              </>
            ) : (
              <>
                <svg className="h-5 w-5 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm">
                  {minWatchPercentage
                    ? `Please watch at least ${minWatchPercentage}% of the video to continue`
                    : 'Please watch the video to continue'}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </QuestionWrapper>
  );
}
